import { Language } from '@shared/types'
import { getLanguage } from '@ui-common/utils'
import { ExternalLink } from '@ui-components/actions/ExternalLink'
import { MediumSectionContent } from '@ui-components/containers/MediumSectionContent'
import { PaperContainer } from '@ui-components/containers/PaperContainer'
import { TightSectionContent } from '@ui-components/containers/TightSectionContent'
import { Divider } from '@ui-components/decorative/Divider'
import { IndentationText } from '@ui-components/decorative/IndentationText'
import { CenteredFlexColumnContainer, FlexColumnContainer } from '@ui-components/global/CommonStyling'
import { SPACE_MEDIUM } from '@ui-components/global/spacing'
import { IntroSection } from '@ui-components/layout/IntroSection'
import { LargeScreenContainer } from '@ui-components/layout/LargeScreenContainer'
import { AdditionalText, BodyText, LabelText, LeadText } from '@ui-components/typography/Typography'
import { FormattedMessage, IntlShape } from 'react-intl'
import styled from 'styled-components'

const IndentedCostsList = styled(IndentationText)`
  display: flex;
  flex-direction: column;
  gap: ${SPACE_MEDIUM};
`

export const MarketingFooter = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => {
  const cost = 0
  return (
    <>
      <PaperContainer $largeScreenSupport={true}>
        <LargeScreenContainer>
          <IntroSection
            title={formatMessage({ id: 'loan-offer-booking-step-footer-content-header' })}
            leadText={formatMessage({ id: 'loan-offer-booking-step-footer-content-lead-text-part1' })}
            headingLevel={2}
            showIntroSectionDivider={false}
          />
          <LeadText>{formatMessage({ id: 'loan-offer-booking-step-footer-content-lead-text-part2' })}</LeadText>
          <ExternalLink
            href={formatMessage({ id: 'loan-offer-booking-step-footer-content-calculator-href' })}
            key={'loan-offer-booking-step-footer-content-calculator-label'}
          >
            {formatMessage({ id: 'loan-offer-booking-step-footer-content-calculator-label' })}
          </ExternalLink>

          <TightSectionContent>
            <LabelText sizing="large">
              {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-services-heading' })}
            </LabelText>
            <BodyText tagName={'p'}>
              {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-services-content' })}
            </BodyText>
            <IndentedCostsList>
              <FlexColumnContainer>
                <LabelText>
                  {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-services-accounts' })}
                </LabelText>
                <LabelText sizing="large">
                  {cost} {formatMessage({ id: 'common-euros-per-month' })}
                </LabelText>
              </FlexColumnContainer>
              <FlexColumnContainer>
                <LabelText>
                  {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-services-cards' })}
                </LabelText>
                <LabelText sizing="large">
                  {cost} {formatMessage({ id: 'common-euros-per-month' })}
                </LabelText>
              </FlexColumnContainer>
              <FlexColumnContainer>
                <LabelText>
                  {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-services-web-credentials' })}
                </LabelText>
                <LabelText sizing="large">
                  {cost} {formatMessage({ id: 'common-euros-per-month' })}
                </LabelText>
              </FlexColumnContainer>
            </IndentedCostsList>
          </TightSectionContent>

          <TightSectionContent>
            <LabelText sizing="large">
              {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-easy-heading' })}
            </LabelText>
            <BodyText tagName={'p'}>
              {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-easy-content-part1' })}
            </BodyText>
            <BodyText tagName={'p'}>
              {formatMessage({ id: 'loan-offer-booking-step-footer-content-banking-easy-content-part2' })}
            </BodyText>
          </TightSectionContent>
        </LargeScreenContainer>
      </PaperContainer>
      <CenteredFlexColumnContainer>
        <TheEndFooter formatMessage={formatMessage} />
      </CenteredFlexColumnContainer>
    </>
  )
}

const CustomFooterWrapper = styled.div`
  div:first-of-type {
    margin: 1rem 0;
  }
  hr {
    margin: 4rem 0 1rem;
  }
`

const TheEndFooter = ({ formatMessage }: { formatMessage: IntlShape['formatMessage'] }) => {
  const userLanguage = getLanguage()
  return (
    <CustomFooterWrapper>
      <MediumSectionContent>
        <LabelText sizing="large">
          <FormattedMessage id="loan-offer-the-end-step-see-also-heading" />
        </LabelText>
        {userLanguage === Language.Fi && (
          <ExternalLink
            href={formatMessage({ id: 'loan-offer-the-end-step-buyers-guide-link' })}
            key={'loan-offer-the-end-step-buyers-guide'}
          >
            {formatMessage({ id: 'loan-offer-the-end-step-buyers-guide-text' })}
          </ExternalLink>
        )}
        <ExternalLink
          href={formatMessage({ id: 'loan-offer-the-end-step-fixed-interest-link' })}
          key={'loan-offer-the-end-step-fixed-interest'}
        >
          {formatMessage({ id: 'loan-offer-the-end-step-fixed-interest-text' })}
        </ExternalLink>
      </MediumSectionContent>
      <Divider />
      <MediumSectionContent>
        <AdditionalText>{formatMessage({ id: 'loan-offer-the-end-footer-note' })}</AdditionalText>
      </MediumSectionContent>
    </CustomFooterWrapper>
  )
}
